import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getUserBets, getfancyUserBets } from "../../store/slices/matchSlice";


// ── Styles ────────────────────────────────────────────────────────────────────

const tableWrap = {
  width: '100%',
  overflowX: 'auto',
  marginTop: '1px',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '13px',
  fontFamily: 'Poppins, sans-serif',
};

const th = {
  padding: '4px 8px',
  textAlign: 'left',
  fontWeight: '600',
  fontSize: '13px',
  whiteSpace: 'nowrap',
  color: '#fff',
  background: '#001F3F',
  border: '1.5px solid #ffffff',
  
  height: '22px',
};

const td = {
  padding: '4px 8px',
  fontWeight: '400',
  fontSize: '12px',
  color: '#3A3A3A',
  borderBottom: 'none',
  border: '1.5px solid #ffffff',
  height: '20px',
  whiteSpace: 'nowrap',
};

const sectionHeader = {
  background: '#001F3F',
  color: '#fff',
  padding: '5px 10px',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: '600',
  fontSize: '13px',
  letterSpacing: '0.5px',
  
  
};


const emptyStateStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '150px',
  color: '#888',
  fontFamily: 'Poppins, sans-serif',
  fontSize: '13px',
  width: '100%',
};



const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).replace(',', '');
};

const rowBg = (betType) => {
  const type = (betType || '').toUpperCase();
  return (type === 'LAY' || type === 'NO')
    ? '#FAC9D1'   
    : '#A6D8FF';  
};
const classifyBet = (bet) => {
  const market = (bet.marketType || bet.market || bet.marketName || '').trim().toUpperCase();
  const sel = (bet.selection || bet.runner || bet.runnerName || '').toLowerCase();
  const isFancy =
    market.includes('FANCY') ||
    market.includes('SESSION') ||
    sel.includes('over') ||
    !!sel.match(/\d+\s*(run|runs|wkt|wicket|fall|yes|no)/i);
  return isFancy ? 'fancy' : 'match';
};

// ── Main Component ────────────────────────────────────────────────────────────
const MyBetsScreen = ({ gmid, onBetCountChange, roundId, casinoBets = [] }) => {
  const dispatch = useDispatch();
  const { userId } = useSelector((state) => state.auth);


  const { getuserbetsdata = [], getfancybetsdata = [] } = useSelector((state) => state.match || {});

  // ── Polling ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId || (!gmid && !roundId)) return;
    const fetchBets = async () => {
      try {
        if (gmid) {
          await dispatch(getUserBets({ userId, matchId: gmid })).unwrap();
          await dispatch(getfancyUserBets({ userId, matchId: gmid })).unwrap();
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchBets();
    const interval = setInterval(fetchBets, 2000);
    return () => clearInterval(interval);
  }, [dispatch, userId, gmid, roundId]);

  // ── Merge + dedupe ────────────────────────────────────────────────────────
  const allBets = useMemo(() => {
    const combined = [...getuserbetsdata, ...getfancybetsdata];
    const seen = new Set();
    return combined.filter(bet => {
      const key = JSON.stringify({
        selection: bet.selection || bet.runnerName,
        odds: bet.odds || bet.rate,
        stake: bet.stake,
        betType: bet.betType,
        placedAt: bet.placedAt || bet.date || bet.createdAt,
      });
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [getuserbetsdata, getfancybetsdata]);

  const matchOddsBets = useMemo(() =>
    allBets.filter(b => classifyBet(b) === 'match')
      .sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt)),
    [allBets]);

  const fancyBets = useMemo(() =>
    allBets.filter(b => classifyBet(b) === 'fancy')
      .sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt)),
    [allBets]);

  // ── Use dummy if real data empty ──────────────────────────────────────────
 const finalMatchBets = matchOddsBets;
  const finalFancyBets = fancyBets;
const filteredCasinoBets = useMemo(() => {
    if (!casinoBets?.length) return [];
    if (!roundId) return [];

    return casinoBets.filter(bet => {
        const betRound = bet.roundId ?? bet.roundID ?? bet.round_id ?? roundId;
        return String(betRound) === String(roundId);
    });
}, [casinoBets, roundId]);

const totalBetCount = finalMatchBets.length + finalFancyBets.length + filteredCasinoBets.length;

useEffect(() => {
    onBetCountChange?.(totalBetCount);
}, [totalBetCount, onBetCountChange]);

  // ── Casino Table ──────────────────────────────────────────────────────────
  const renderCasinoTable = () => {
    if (!filteredCasinoBets.length) return null;
    return (
      <Box>
        <Box style={sectionHeader}>Casino Bets</Box>
        <Box style={tableWrap}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>No</th>
                <th style={th}>Market</th>
                <th style={th}>Bet Type</th>
                <th style={th}>Stake</th>
                <th style={th}>Exposure</th>
                <th style={th}>Date & Time</th>
                <th style={th}>IP</th>
              </tr>
            </thead>
            <tbody>
              {filteredCasinoBets.map((bet, i) => (
                <tr key={bet.betId ?? i} style={{ background: rowBg(bet.betType), borderBottom: '1.5px solid #E8E8E8' }}>
                  <td style={td}>{i + 1}</td>
                  <td style={td}>{bet.selection || '—'}</td>
                 <td
  style={{
    ...td,
    background: rowBg(bet.betType),
  }}
>
  {(bet.betType || '').toUpperCase() === 'LAY' ? 'LAY' : 'BACK'}
</td>
                  <td style={td}>{bet.stake || '—'}</td>
                  <td style={{ ...td, color: 'red' }}>{bet.exposure ?? '—'}</td>
                  <td style={td}>{formatDate(bet.date)}</td>
                  <td style={td}>{bet.ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Box>
    );
  };

  // ── Match / Fancy Table ───────────────────────────────────────────────────
  const renderTable = (title, bets, isFancy = false) => {
    if (!bets || !bets.length) return null;
    return (
      <Box >
        <Box style={sectionHeader}>{title}</Box>
        <Box style={tableWrap}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>No</th>
                <th style={th}>Runner Name</th>
                <th style={th}>Rate</th>
                <th style={th}>Stake</th>
                <th style={th}>Market</th>
                <th style={th}>{isFancy ? 'Yes/No' : 'Back/Lay'}</th>
                <th style={th}>Exp</th>
                <th style={th}>Date & Time</th>
                <th style={th}>IP</th>
              </tr>
            </thead>
            <tbody>
              {bets.map((bet, i) => (
                <tr key={i} style={{ background: rowBg(bet.betType), borderBottom: '1.5px solid #ffffff' }}>
                  <td style={td}>{i + 1}</td>
                  <td style={td}>
                    {bet.selection || bet.runnerName || bet.runner || '—'}
                    {['TOSS', 'TIE', 'BOOKMAKER'].includes((bet.marketType || '').toUpperCase())
                      ? ` (${bet.marketType})` : ''}
                  </td>
                  <td style={td}>
                    {isFancy
                      ? `${bet.sessionValue || bet.rate || '—'} (${bet.odds || '—'})`
                      : bet.odds || bet.rate || '—'}
                  </td>
                  <td style={td}>{bet.stake || '—'}</td>
                  <td style={td}>{bet.marketType || '—'}</td>
                  <td style={td}>{bet.betType || '—'}</td>
                  <td style={{ ...td, color: 'red' }}>{bet.liability || '—'}</td>
                  <td style={td}>{formatDate(bet.placedAt)}</td>
                  <td style={td}>{bet.ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Box>
    );
  };

  // ✅ NEW: Empty state renderer (keeps design language consistent)
  const renderEmptyState = () => (
    <Box style={emptyStateStyle}>
      No bets placed yet
    </Box>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: '100%', pb: 2, minHeight: '150px',}}>
      {totalBetCount === 0 ? (
        renderEmptyState()
      ) : (
        <>
          {renderTable('Match Bets', finalMatchBets, false)}
          {renderTable('Fancy Bets', finalFancyBets, true)}
          {renderCasinoTable()}
        </>
      )}
    </Box>
  );
};

export default MyBetsScreen;